import {Injectable} from '@angular/core';
import {AppConfigService} from '@npck/libs/commons';
import {LocalStorageService} from 'ngx-webstorage';
import {v4 as uuidV4} from 'uuid';

const DEVICE_ID_KEY = 'deviceId';
const UNDEFINED_VALUE = 'UNDEFINED';

@Injectable({
    providedIn: 'root',
})
export class SessionManager {

    session: Session;

    private deviceId: string;

    constructor(localStorageService: LocalStorageService) {
        this.deviceId = localStorageService.retrieve(DEVICE_ID_KEY);
        if (!this.deviceId) {
            this.deviceId = uuidV4();
            localStorageService.store(DEVICE_ID_KEY, this.deviceId);
        }
    }

    start(userId?: string): void {
        this.session = this.createSession(userId);
    }

    setUserId(userId: string): void {
        this.session.userId = userId;
    }

    finish(): void {
        this.session.sessionFinish = this.getCurrentTime();
    }

    getCurrentTime(): Date {
        return new Date(new Date().getTime() - AppConfigService.serverTimeDiff);
    }

    private createSession(userId?: string): Session {
        const date = this.getCurrentTime();
        return this.session = {
            id: uuidV4(),
            userId: userId,
            deviceId: this.deviceId,
            deviceType: this.getDeviceType(),
            deviceOs: navigator.platform,
            osVersion: navigator.appVersion,
            browserVersion: this.getBrowserVersion(),
            screen: screen.width.toString(),
            language: navigator.language || UNDEFINED_VALUE,
            referral: document.referrer,
            sessionStart: date,
            userAgent: navigator.userAgent,
        };
    }

    private getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
        const userAgent = navigator.userAgent;
        const width = window.innerWidth;
        if (/Mobi|Android/i.test(userAgent) || width <= 768) {
            return 'mobile';
        } else if (/Tablet|iPad/i.test(userAgent) || (width > 768 && width <= 1024)) {
            return 'tablet';
        } else {
            return 'desktop';
        }
    }

    private getBrowserVersion(): string {
        const userAgent = navigator.userAgent;
        let tem;
        let match = userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        if (/trident/i.test(match[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(userAgent) || [];
            return 'IE ' + (tem[1] || '');
        }
        if (match[1] === 'Chrome') {
            tem = userAgent.match(/\b(OPR|Edge)\/(\d+)/);
            if (tem != null) {
                return tem.slice(1).join(' ').replace('OPR', 'Opera');
            }
        }
        match = match[2] ? [match[1], match[2]] : [navigator.appName, navigator.appVersion, '-?'];
        if ((tem = userAgent.match(/version\/(\d+)/i)) != null) {
            match.splice(1, 1, tem[1]);
        }
        const result = match.join(' ');

        if (!result || result.trim() == '') {
            return UNDEFINED_VALUE;
        }

        return result;
    }
}

export interface Session {
    id: string;
    userId?: string;
    deviceId: string;
    deviceType: string;
    deviceOs: string;
    osVersion: string;
    browserVersion: string;
    screen: string;
    language: string;
    referral: string;
    sessionStart: Date;
    sessionFinish?: Date;
    userAgent: string;
}
